import { Button } from "@/components/ui/button";
import { CarFront, Sparkles, Shield, Star, ImageIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOptimizedImg } from "@/hooks/useOptimizedImg";

interface Props {
  onStart: () => void;
  /** Promote the left banner image to eager + high priority. Set only on
   *  surfaces where this banner is in the initial viewport (desktop home),
   *  otherwise it competes with the real hero LCP. */
  priority?: boolean;
}

interface BannerContent {
  image?: string;
  button_url?: string;
  title?: string;
  button_text?: string;
}

export default function RVExpertBanner({ onStart, priority = false }: Props) {
  const { data: banner } = useQuery({
    queryKey: ["rex-banner-left"],
    queryFn: async () => {
      const { data } = await supabase
        .from("page_content")
        .select("content")
        .eq("page_slug", "home")
        .eq("section_key", "rex_banner_left")
        .eq("is_published", true)
        .maybeSingle();
      return (data?.content as BannerContent) || null;
    },
    staleTime: 30 * 60 * 1000,
  });

  const imageUrl = banner?.image?.trim();
  const linkUrl = banner?.button_url?.trim();
  const altText = banner?.title?.trim() || "Promotional banner";
  const optimizedSrc = useOptimizedImg(imageUrl, "category", { q: 45 });

  const bannerImg = imageUrl ? (
    <img
      src={optimizedSrc}
      alt={altText}
        width={400}
        height={225}
      className="h-full w-full rounded-xl object-cover"
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={priority ? "high" : "low"}
    />
  ) : (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/20 bg-card/50 p-6 text-center">
      <ImageIcon className="h-8 w-8 text-muted-foreground/40" />
      <p className="text-xs text-muted-foreground">
        Add a promotional banner from{" "}
        <span className="font-semibold">Admin → Pages → Home → "Promotional Banner"</span>
      </p>
    </div>
  );

  return (
    <section
      id="rex"
      className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-y scroll-mt-24"
    >
      <div className="container py-8 md:py-10">
        <div className="mx-auto grid max-w-6xl items-center gap-6 md:grid-cols-2 md:gap-8">
          {/* Left: Editable banner — aspect locked to prevent CLS */}
          <div className="aspect-[16/9] w-full overflow-hidden rounded-xl">
            {linkUrl ? (
              <a href={linkUrl} className="block h-full w-full transition hover:opacity-90">
                {bannerImg}
              </a>
            ) : (
              bannerImg
            )}
          </div>

          {/* Right: Meet Rex */}
          <div className="text-center md:text-left">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase text-primary-foreground mb-4">
              <Sparkles className="h-3 w-3" /> NEW
            </span>
            <h2 className="font-heading text-2xl font-black uppercase text-foreground md:text-3xl">
              Meet <span className="text-primary">Rex</span>, Your AI RV Expert
            </h2>
            <p className="mt-2 text-muted-foreground md:text-lg">
              Skip the endless browsing. I'll find your perfect RV match immediately.
            </p>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground md:justify-start">
              <span className="inline-flex items-center gap-1"><Shield className="h-3.5 w-3.5 text-primary" /> 100% Free</span>
              <span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5 text-primary" /> Custom Results</span>
              <span className="inline-flex items-center gap-1"><CarFront className="h-3.5 w-3.5 text-primary" /> No Obligation</span>
            </div>

            <Button
              size="lg"
              onClick={onStart}
              className="mt-6 font-heading uppercase tracking-wide gap-2"
            >
              <CarFront className="h-4 w-4" /> Start Finding My Perfect RV
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
